import { useEffect, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { authAPI, setApiAccessToken } from '@/api/client'

/**
 * Hook to handle OAuth callback parameters from URL fragment
 * Backend redirects to /dashboard#accessToken=...&expiresInSeconds=...
 *
 * This hook:
 * 1. Parses fragment parameters on mount
 * 2. Fetches user profile with the access token
 * 3. Updates AuthContext
 * 4. Clears the fragment from URL
 */
export function useOAuthCallback() {
  const { login } = useAuth()
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Only process once
    if (hasProcessed.current) {
      return
    }

    const handleCallback = async () => {
      try {
        // Parse URL fragment
        const hash = window.location.hash.substring(1) // Remove #
        if (!hash) {
          return
        }

        const params = new URLSearchParams(hash)
        const accessToken = params.get('accessToken')
        const expiresInSeconds = params.get('expiresInSeconds')
        const tokenType = params.get('tokenType')

        if (!accessToken) {
          return
        }

        hasProcessed.current = true

        // Temporarily set the access token so we can fetch the profile
        setApiAccessToken(accessToken)

        // Fetch user profile
        const profile = await authAPI.getProfile()

        const expiresAt = (() => {
          if (!expiresInSeconds) {
            return null
          }
          const seconds = Number(expiresInSeconds)
          if (Number.isNaN(seconds)) {
            return null
          }
          return new Date(Date.now() + seconds * 1000)
        })()

        // Update auth state
        login({
          accessToken,
          user: profile.data,
          expiresAt,
          tokenType: tokenType ?? 'access',
        })

        // Signal to Playwright E2E helpers that auth hydration is complete.
        // This is a no-op for normal users; tests wait on this flag.
        ;(
          window as typeof window & { __WHITE_GI_AUTH_READY?: boolean }
        ).__WHITE_GI_AUTH_READY = true

        // Clear fragment from URL
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        )
      } catch (error) {
        console.error('OAuth callback error:', error)
        setApiAccessToken(null)
        // Clear fragment on error
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        )
      }
    }

    void handleCallback()
  }, [login])
}
